import { defaultAddStatus, defaultAddOwnership } from "../utils/settings.js";
import { getDB } from "./schema.js";
import { todayDate, nowIso } from "../utils/dates.js";
import { AWARDS, BOOK_FORMATS, TIMES_FINISHED } from "../components/status-badge.js";
import { queueCloudWrite } from "./sync.js";
import { logItemChange } from "./history.js";
import { withOverride, withoutOverride, applyCatalogFields } from "../utils/overrides.js";

const STORE = "books";

// Books reuse the shared note cap; kept local so books don't depend on games.
export const NOTE_MAX_LENGTH = 280;

function uuid() {
	if (globalThis.crypto?.randomUUID) return crypto.randomUUID();
	return "xxxxxxxx-xxxx-4xxx-yxxx-xxxxxxxxxxxx".replace(/[xy]/g, (c) => {
		const r = (Math.random() * 16) | 0;
		const v = c === "x" ? r : (r & 0x3) | 0x8;
		return v.toString(16);
	});
}

export async function listBooks() {
	const db = await getDB();
	return db.getAll(STORE);
}

export async function getBookById(id) {
	const db = await getDB();
	return db.get(STORE, id);
}

export async function getBookByOlId(olId) {
	const db = await getDB();
	return db.getFromIndex(STORE, "ol_id", olId);
}

export async function addBook({
	ol_id,
	title,
	authors = [],
	published_year = null,
	cover_url = null,
	subjects = [],
	summary = null,
	engagement_status = defaultAddStatus(),
	ownership_status = defaultAddOwnership(),
}) {
	const existing = await getBookByOlId(ol_id);
	if (existing) return existing;

	const now = nowIso();
	const today = todayDate();
	const record = {
		id: uuid(),
		ol_id,
		title,
		authors,
		published_year,
		cover_url,
		subjects,
		summary,
		media_type: "book",
		engagement_status,
		ownership_status,
		formats: [],
		rating: null,
		awards: [],
		note: null,
		// Specific editions the user owns (snapshots from the editions endpoint).
		// The work record is "I've read this"; owned_editions is "I have these
		// copies". Empty until the user picks editions on the detail screen.
		owned_editions: [],
		// Optional: a user-chosen cover (e.g. an owned edition's art) that overrides
		// the source cover for display. null = use the source `cover_url`.
		cover_override_url: null,
		times_finished: null,
		// Canonical column the app no longer uses (times_finished replaced it);
		// kept at 0 so the synced cloud row stays valid.
		playthrough_count: 0,
		date_first_completed: engagement_status === "done" ? today : null,
		last_engaged_date:
			engagement_status === "playing" ||
			engagement_status === "replaying" ||
			engagement_status === "done"
				? today
				: null,
		// Forward-looking; no UI yet (friends are deferred).
		visibility: "friends",
		created_at: now,
		updated_at: now,
	};
	const db = await getDB();
	await db.add(STORE, record);
	await queueCloudWrite({ type: "create", mediaType: "book", record });
	await logItemChange("book", null, record);
	return record;
}

export async function updateBook(id, patch) {
	const db = await getDB();
	const current = await db.get(STORE, id);
	if (!current) throw new Error(`Book ${id} not found`);

	const next = { ...current, ...patch, updated_at: nowIso() };

	if (
		patch.engagement_status &&
		patch.engagement_status !== current.engagement_status
	) {
		const today = todayDate();
		if (
			patch.engagement_status === "playing" ||
			patch.engagement_status === "replaying"
		) {
			next.last_engaged_date = today;
		}
		if (patch.engagement_status === "done") {
			next.last_engaged_date = today;
			if (!current.date_first_completed) {
				next.date_first_completed = today;
			}
		}
	}

	await db.put(STORE, next);
	await queueCloudWrite({ type: "update", mediaType: "book", record: next });
	await logItemChange("book", current, next);
	return next;
}

export async function setBookFormats(id, list) {
	if (!Array.isArray(list)) throw new Error("formats must be an array");
	const valid = new Set(BOOK_FORMATS);
	return updateBook(id, { formats: list.filter((v) => valid.has(v)) });
}

export async function setBookRating(id, rating) {
	if (
		rating !== null &&
		(!Number.isInteger(rating) || rating < 1 || rating > 5)
	) {
		throw new Error("Rating must be 1-5 or null");
	}
	return updateBook(id, { rating });
}

export async function setBookTimesFinished(id, value) {
	if (value !== null && !TIMES_FINISHED.some((b) => b.value === value)) {
		throw new Error(`Invalid times_finished bucket: ${value}`);
	}
	return updateBook(id, { times_finished: value });
}

export async function setBookAwards(id, list) {
	if (!Array.isArray(list)) throw new Error("awards must be an array");
	const valid = new Set(AWARDS.map((a) => a.value));
	return updateBook(id, { awards: list.filter((v) => valid.has(v)) });
}

export async function setBookNote(id, note) {
	const trimmed = (note ?? "").trim().slice(0, NOTE_MAX_LENGTH);
	return updateBook(id, { note: trimmed === "" ? null : trimmed });
}

// Keep only the fields we rely on, so a stored owned-edition is a stable, small
// snapshot regardless of what the editions endpoint adds later.
function sanitizeBookEdition(e) {
	if (!e || typeof e !== "object" || !e.edition_id) return null;
	return {
		edition_id: String(e.edition_id),
		title: e.title ?? null,
		publishers: Array.isArray(e.publishers) ? e.publishers.filter(Boolean) : [],
		publish_year: Number.isInteger(e.publish_year) ? e.publish_year : null,
		physical_format: e.physical_format ?? null,
		isbn: e.isbn ?? null,
		cover_url: e.cover_url ?? null,
	};
}

export async function setBookOwnedEditions(id, list) {
	if (!Array.isArray(list)) throw new Error("owned_editions must be an array");
	const clean = list.map(sanitizeBookEdition).filter(Boolean);
	return updateBook(id, { owned_editions: clean });
}

// The cover shown for a book: a user-chosen edition cover overrides the source
// (Open Library) cover; falls back to the source cover, then nothing.
export function bookDisplayCover(book) {
	return book?.cover_override_url ?? book?.cover_url ?? null;
}

// Pick which cover represents the book — pass an owned edition's cover_url, or
// null to fall back to the original source cover. Goes through the generic
// override layer (utils/overrides.js): the chosen cover lands in `cover_url` with
// the source cover parked in `source_values`. The legacy `cover_override_url` is
// retired on write (still read as a fallback for records that predate this).
export async function setBookCoverOverride(id, url) {
	const db = await getDB();
	const current = await db.get(STORE, id);
	if (!current) throw new Error(`Book ${id} not found`);
	const next = url
		? withOverride(current, "cover_url", url)
		: withoutOverride(current, "cover_url");
	return updateBook(id, {
		cover_url: next.cover_url ?? null,
		source_values: next.source_values ?? {},
		cover_override_url: null,
	});
}

export async function deleteBook(id) {
	const db = await getDB();
	// Read first so the cloud delete op can carry the external id (ol_id).
	const current = await db.get(STORE, id);
	await db.delete(STORE, id);
	if (current) {
		await queueCloudWrite({ type: "delete", mediaType: "book", record: current });
		await logItemChange("book", current, null);
	}
}

export async function deleteBookByOlId(olId) {
	const existing = await getBookByOlId(olId);
	if (!existing) return false;
	await deleteBook(existing.id);
	return true;
}

// Lazy backfill of the work summary/subjects, fetched from the detail endpoint
// the first time a book's page is opened (search results don't include them).
export async function backfillBookSummary(id, { summary, subjects }) {
	const db = await getDB();
	const current = await db.get(STORE, id);
	if (!current) return null;
	const patch = {};
	if (current.summary == null && summary) patch.summary = summary;
	if ((current.subjects ?? []).length === 0 && subjects?.length) {
		patch.subjects = subjects;
	}
	if (Object.keys(patch).length === 0) return current;
	// Through the override layer, so a field the user edited is never clobbered.
	const { next: applied } = applyCatalogFields(current, patch);
	const next = { ...applied, updated_at: nowIso() };
	await db.put(STORE, next);
	await queueCloudWrite({ type: "update", mediaType: "book", record: next });
	return next;
}
